// src/components/Inquiry/items/OtherLineRow.tsx
//
// A line whose product is an OTHER product: no model, no drawing, no material
// - a name, a free-text description and whatever files were attached. Since
// EE-253 this is also what a Requisition-born line looks like, so it is the
// common case on the sourcing board rather than the exception it once was.
//
// Read mode also covers the one line shape that has no product at all: a
// Requisition converted before EE-253 wrote the material/tool spec into the
// inquiry line's `comment` and left the product null. That used to be a third
// row component of its own; it is the same four cells with the spec read from
// a different field, so it is the `product === null` branch here.
import { FunctionComponent, ReactNode, useEffect, useState, useContext } from "react";
import {
  Badge,
  IconButton,
  SvgIcon,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, FileUpload } from "@mui/icons-material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import FileResourceList from "components/FileResourceList";
import Modal from "components/Modals/Modal";
import { apiUpdateProduct } from "util/network/Products";
import { FileResourceType } from "types/FileResourceTypes";
import { CartItemType } from "types/CartTypes";
import { OrderItemType } from "types/OrderTypes";
import { InquiryTypeEnum, isMaterialInquiry } from "types/InquiryTypes";
import {
  OtherProductDetailsType,
  UpdateOtherProductType,
} from "types/products/ProductCommandsType";
import {
  CELL_PRIMARY,
  CELL_SECONDARY,
  STACKED_CELL,
} from "components/Dashboard/dashboardTable";
import {
  EMPTY,
  INQUIRY_ITEM_COLUMNS,
  SELECT_COLUMN,
  itemCellProps,
  showsQuantity,
} from "./inquiryItemColumns";
import SpecCell from "./SpecCell";
import { InquiryLine, formatLineQuantity } from "./inquiryLine";

type CommonProps = {
  inquiryType?: InquiryTypeEnum;
};
type ReadProps = CommonProps & {
  mode: "read";
  line: InquiryLine;
  /** Cells after Qty - the goods-in counts, once there are any. */
  trailing?: ReactNode;
};
type EditProps = CommonProps & {
  mode: "edit";
  item: OrderItemType | CartItemType;
  onUpdate: (item: OrderItemType | CartItemType) => void;
  onDelete: (item: OrderItemType | CartItemType) => void;
};

const MUTED = { ...CELL_PRIMARY, color: "var(--black75)" };

// ─── Read ────────────────────────────────────────────────────────────────────

const ReadRow: FunctionComponent<Omit<ReadProps, "mode">> = ({
  line,
  trailing,
  inquiryType,
}) => {
  const details = line.product?.details as OtherProductDetailsType | undefined;
  const files = details?.fileResources ?? [];
  const [showFiles, setShowFiles] = useState(false);

  return (
    <TableRow>
      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.item)}>
        {/* The product id means nothing on a material line - the product is
            one made up to carry the requisition's spec - so the name (material
            - profile) is all there is to say. */}
        {line.product && isMaterialInquiry(inquiryType) ? (
          <Typography sx={CELL_PRIMARY}>{line.product.name}</Typography>
        ) : line.product ? (
          <span style={STACKED_CELL}>
            <Typography sx={CELL_PRIMARY}>#{line.product.id}</Typography>
            <Typography sx={CELL_SECONDARY}>{line.product.name}</Typography>
          </span>
        ) : (
          <Typography sx={MUTED}>{EMPTY}</Typography>
        )}
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.spec)}>
        <SpecCell lines={[details?.description || line.comment || EMPTY]} />
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.files)}>
        {files.length === 0 ? (
          <Typography sx={MUTED}>{EMPTY}</Typography>
        ) : (
          <>
            <Tooltip
              title={`${files.length} file${files.length === 1 ? "" : "s"}`}
            >
              <IconButton size="small" onClick={() => setShowFiles(true)}>
                <Badge badgeContent={files.length} color="primary">
                  <SvgIcon fontSize="small">
                    <FileUpload />
                  </SvgIcon>
                </Badge>
              </IconButton>
            </Tooltip>
            <Modal isOpen={showFiles} onClose={() => setShowFiles(false)}>
              <div style={{ height: "100%", overflow: "auto" }}>
                <FileResourceList resources={files} />
              </div>
            </Modal>
          </>
        )}
      </TableCell>

      {showsQuantity(inquiryType) && (
        <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.qty)}>
          <Typography sx={CELL_PRIMARY}>{formatLineQuantity(line)}</Typography>
        </TableCell>
      )}
      {trailing}
    </TableRow>
  );
};

// ─── Edit ────────────────────────────────────────────────────────────────────

const EditRow: FunctionComponent<Omit<EditProps, "mode">> = ({
  item,
  onUpdate,
  onDelete,
  inquiryType,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  // The line carries its product inline, so there is nothing to fetch - this
  // used to be an apiGetProduct per row on mount, keyed by the item id in the
  // hope that it was also the product id.
  const [product, setProduct] = useState(item.product);
  const details = product.details as OtherProductDetailsType | undefined;

  const [qty, setQty] = useState<number>(item.quantity || 1);
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(details?.description ?? "");
  const [showFiles, setShowFiles] = useState(false);

  const files: FileResourceType[] = details?.fileResources ?? [];

  useEffect(() => {
    setProduct(item.product);
    setName(item.product.name);
    setDescription(
      (item.product.details as OtherProductDetailsType | undefined)
        ?.description ?? ""
    );
  }, [item.product]);

  // These write straight through to the product on blur - there is no dirty
  // state and no Save behind them. That makes reporting a failure the only
  // thing standing between a rejected write and a field that silently snaps
  // back: update_other_product answers 401 when the product was created by
  // someone else and the user is below MANAGER, which is not rare.
  // Resolves to whether the write landed: FileResourceList takes that as its
  // cue to keep or drop the file it just added to its own list.
  const updateProduct = (changes: UpdateOtherProductType): Promise<boolean> =>
    apiUpdateProduct(token, product.id, changes)
      .then((res) => {
        if (!res) return false;
        setProduct(res);
        onUpdate({ ...item, product: res });
        return true;
      })
      .catch((err) => {
        addToast({
          type: "error",
          message: `This line could not be saved: ${err}`,
          keep: true,
        });
        // Put the inputs back to what is actually stored, so what is on screen
        // is not a change that never landed.
        setName(product.name);
        setDescription(details?.description ?? "");
        return false;
      });

  return (
    <TableRow hover selected>
      <TableCell {...itemCellProps(SELECT_COLUMN)} padding="checkbox">
        <IconButton size="small" onClick={() => onDelete(item)}>
          <Delete />
        </IconButton>
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.item)}>
        <TextField
          fullWidth
          size="small"
          variant="standard"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={(e) => updateProduct({ name: e.target.value })}
        />
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.spec)}>
        <TextField
          fullWidth
          multiline
          size="small"
          variant="standard"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={(e) => updateProduct({ description: e.target.value })}
        />
      </TableCell>

      <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.files)}>
        <Tooltip title="Upload files">
          <IconButton size="small" onClick={() => setShowFiles(true)}>
            <Badge badgeContent={files.length} color="primary">
              <SvgIcon fontSize="small">
                <FileUpload />
              </SvgIcon>
            </Badge>
          </IconButton>
        </Tooltip>
        <Modal isOpen={showFiles} onClose={() => setShowFiles(false)}>
          <div style={{ height: "100%", overflow: "auto" }}>
            <FileResourceList
              resources={files}
              allowEdit
              onChange={(res) =>
                updateProduct({ fileResources: res.map((r) => r.id) })
              }
            />
          </div>
        </Modal>
      </TableCell>

      {showsQuantity(inquiryType) && (
        <TableCell {...itemCellProps(INQUIRY_ITEM_COLUMNS.qty)}>
          <TextField
            size="small"
            variant="standard"
            type="number"
            value={qty}
            onChange={(e) => setQty(parseInt(e.target.value))}
            onBlur={(e) => {
              const newQty = parseInt(e.target.value);
              if (!isNaN(newQty) && newQty > 0)
                onUpdate({ ...item, quantity: newQty });
              else setQty(item.quantity || 1);
            }}
          />
        </TableCell>
      )}
    </TableRow>
  );
};

const OtherLineRow: FunctionComponent<ReadProps | EditProps> = (props) =>
  props.mode === "read" ? (
    <ReadRow
      line={props.line}
      trailing={props.trailing}
      inquiryType={props.inquiryType}
    />
  ) : (
    <EditRow
      item={props.item}
      onUpdate={props.onUpdate}
      onDelete={props.onDelete}
      inquiryType={props.inquiryType}
    />
  );

export default OtherLineRow;
